/**
 * Seeded random numbers. Every randomised decision in Quest Jump goes through
 * this so a level is the same tower for every player, every time (CLAUDE.md).
 *
 * Pure logic: no Phaser imports.
 */

export interface Rng {
  /** Next float in [0, 1). */
  next(): number;
  /** Integer in [min, max], both ends inclusive. */
  int(min: number, max: number): number;
  /** Uniform element from a non-empty array. */
  pick<T>(arr: readonly T[]): T;
  /** New array, Fisher-Yates shuffled. Never mutates the input. */
  shuffle<T>(arr: readonly T[]): T[];
  /** True with probability p. chance(0) is never, chance(1) is always. */
  chance(p: number): boolean;
}

/**
 * mulberry32: 32-bit state, fast, good enough spread for level layout.
 * Same seed always yields the same sequence.
 */
export function mulberry32(seed: number): Rng {
  let state = seed >>> 0;

  const next = (): number => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const int = (min: number, max: number): number => {
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      throw new RangeError('rng.int() needs finite bounds');
    }
    const lo = Math.ceil(Math.min(min, max));
    const hi = Math.floor(Math.max(min, max));
    if (hi < lo) throw new RangeError(`rng.int() has no integer in [${min}, ${max}]`);
    return lo + Math.floor(next() * (hi - lo + 1));
  };

  const pick = <T>(arr: readonly T[]): T => {
    if (arr.length === 0) throw new RangeError('rng.pick() on an empty array');
    return arr[int(0, arr.length - 1)] as T;
  };

  const shuffle = <T>(arr: readonly T[]): T[] => {
    const out = arr.slice();
    for (let i = out.length - 1; i > 0; i -= 1) {
      const j = int(0, i);
      const a = out[i] as T;
      const b = out[j] as T;
      out[i] = b;
      out[j] = a;
    }
    return out;
  };

  const chance = (p: number): boolean => {
    if (p <= 0) return false;
    if (p >= 1) return true;
    return next() < p;
  };

  return { next, int, pick, shuffle, chance };
}
